import React, { useEffect, useState } from 'react';
import { fadeTimeout, getOverallPadding } from '../helpers/Themes';
import { Button, Fade, Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import {
    GetUploadsParams,
    GetUploadsResponse,
    Upload,
    UploadOrderByFields,
    User,
    UserProfile,
    UserProfileDefaultDescription,
} from '../workers/ApiWorker';
import Card from '@mui/material/Card';
import PageTopSection from '../components/universal/pageTopSection/PageTopSection';
import { useLocation, useParams } from 'react-router-dom';
import { useApi } from '../contexts/ApiContext';
import NoResultsFound from '../components/universal/loader/NoResultsFound';
import { createWorkerFactory, useWorker } from '@shopify/react-web-worker';
import { upperCaseFirst } from 'upper-case-first';
import { PreviewCardSizeOthers } from '../helpers/UploadCardSizings';
import SearchResults from '../components/search/SearchResults';
import { useCustomTheme } from '../contexts/ThemeContext';
import Socials from '../helpers/Socials';
import UpdateUserModal from '../components/modal/UpdateUserModal';
import { FOLLOW_USER_FAILED } from '../helpers/Messages';
import UserProfileImage from '../components/user/UserProfileImage';
import SuspendUsersModal from '../components/modal/SuspendUsersModal';
import FollowingUsersModal from '../components/modal/FollowingUsersModal';
import FollowersUsersModal from '../components/modal/FollowersUsersModal';

const createApiWorker = createWorkerFactory(
    () => import('../workers/ApiWorker')
);

interface ViewUserPageProps {
    isMobile: boolean;
    padding?: number;
    user?: User;
    width?: number;
}

const CACHE_KEY_PREFIX = 'userPageParams_';

const ViewUserPage: React.FC<ViewUserPageProps> = ({
    isMobile,
    padding,
    user,
    width,
}) => {
    const apiWorker = useWorker(createApiWorker);

    const { theme } = useCustomTheme();
    const location = useLocation();
    const { username } = useParams<{ username: string }>();
    const { userProfileUuid, fresh } = location.state || {};

    const { token } = useApi();

    const [pageLoaded, setPageLoaded] = useState<boolean>(true);
    const [noResultsFound, setNoResultsFound] = useState<boolean>(false);
    const [loadingUser, setLoadingUser] = useState<boolean>(true);
    const [loadedUser, setLoadedUser] = useState<UserProfile | null>(null);
    const [isLoadedUserMe, setIsLoadedUserMe] = useState<boolean>(false);
    const [followers, setFollowers] = useState<number>(0);
    const [following, setFollowing] = useState<number>(0);
    const [userFollowing, setUserFollowing] = useState<boolean>(false);
    const [uploadImage, setUploadImage] = useState<File | undefined>(undefined);
    const [loadedUserImage, setLoadedUserImage] = useState<string | null>(null);
    const [error, setError] = useState<string | undefined>(undefined);
    const [success, setSuccess] = useState<string | undefined>(undefined);

    const [isFollowersUsersModalOpen, setIsFollowersUsersModalOpen] =
        useState(false);
    const [isFollowingUsersModalOpen, setIsFollowingUsersModalOpen] =
        useState(false);
    const [isUpdateUserModalOpen, setIsUpdateUserModalOpen] = useState(false);

    const showUpdateUserModal = () => {
        setSuccess(undefined);
        setError(undefined);
        setIsUpdateUserModalOpen(true);
    };

    const closeUpdateUserModal = (resetForm?: () => void) => {
        if (resetForm) {
            resetForm();
        }
        setError(undefined);
        setSuccess(undefined);
        setIsUpdateUserModalOpen(false);
    };

    const closeFollowingUsersModal = () => {
        setError(undefined);
        setSuccess(undefined);
        setIsFollowingUsersModalOpen(false);
    };

    const showFollowingUsersModal = () => {
        setError(undefined);
        setSuccess(undefined);
        setIsFollowingUsersModalOpen(true);
    };

    const closeFollowersUsersModal = () => {
        setError(undefined);
        setSuccess(undefined);
        setIsFollowersUsersModalOpen(false);
    };

    const showFollowersUsersModal = () => {
        setError(undefined);
        setSuccess(undefined);
        setIsFollowersUsersModalOpen(true);
    };

    const [loadingResults, setLoadingResults] = useState<boolean>(true);
    const defaultCount = 12;
    const defaultPage = 1;
    const defaultOrderBy = UploadOrderByFields.CREATED_AT;

    const cacheKey =
        CACHE_KEY_PREFIX + (username ?? userProfileUuid ?? 'unknown');

    const getCachedPage = (): number => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const parsed = JSON.parse(cached);
                return parsed.page ?? defaultPage;
            }
        } catch {
            // ignore malformed cache
        }
        return defaultPage;
    };

    const [currentPage, setCurrentPage] = useState<number>(getCachedPage);
    const [lastPage, setLastPage] = useState<number>(defaultPage);

    const uploadParamsDefault: GetUploadsParams = {
        count: defaultCount,
        page: getCachedPage(),
        orderBy: defaultOrderBy,
    };

    const [uploadParams, setUploadParams] =
        useState<GetUploadsParams>(uploadParamsDefault);

    const [uploads, setUploads] = useState<Upload[]>([]);

    const handleUpdateData = (
        response: GetUploadsResponse,
        params: GetUploadsParams
    ) => {
        localStorage.setItem(cacheKey, JSON.stringify({ page: params.page }));
        setUploadParams(params);
        setUploads(response.data);
        setCurrentPage(response.meta.page.current);
        setLastPage(response.meta.page.last);
        setLoadingResults(false);
    };

    const handlePageChange = async (
        event: React.ChangeEvent<unknown>,
        value: number
    ) => {
        setLoadingResults(true);
        setUploads([]);
        const newUploadParams: GetUploadsParams = {
            ...uploadParams,
            page: value,
        };
        const response = await apiWorker.getUploads(token, newUploadParams);
        handleUpdateData(response, newUploadParams);
    };

    useEffect(() => {
        const getUploads = async () => {
            if (loadedUser) {
                setUploads([]);

                const cachedPage = getCachedPage();

                const updatedUploadParams = {
                    ...uploadParamsDefault,
                    page: cachedPage,
                    userProfile: loadedUser.uuid,
                };

                const response = await apiWorker.getUploads(
                    token,
                    updatedUploadParams
                );

                handleUpdateData(response, updatedUploadParams);
            }
        };

        getUploads()
            .then(() => {})
            .catch((error) => {
                console.log('Error fetching uploads:', error);
            });
    }, [loadedUser, isLoadedUserMe]);

    useEffect(() => {
        const getUserForPage = async () => {
            const isFresh = location.state?.fresh;

            if (isFresh) {
                // Consume the fresh flag immediately so back navigation
                // doesn't trigger it again
                window.history.replaceState(
                    { ...location.state, fresh: false },
                    ''
                );
                localStorage.removeItem(cacheKey);
            }

            setPageLoaded(false);
            setLoadedUser(null);
            setLoadingUser(true);
            setNoResultsFound(false);
            setIsLoadedUserMe(false);
            setFollowers(0);
            setFollowing(0);
            setUserFollowing(false);
            setUploadImage(undefined);
            setLoadedUserImage(null);
            setLoadingResults(true);
            setLastPage(defaultPage);
            setUploads([]);

            // Restore cached page for this user on mount
            const cachedPage = getCachedPage();
            setCurrentPage(cachedPage);
            setUploadParams({ ...uploadParamsDefault, page: cachedPage });

            if (token && user && user.userProfile.uuid === userProfileUuid) {
                setLoadedUser(user.userProfile);
                setFollowers(user.userProfile.follows.followers);
                setFollowing(user.userProfile.follows.following);
                setLoadedUserImage(user.userProfile.image.main);
                setUserFollowing(user.userProfile.userFollowing ?? false);
                setLoadingUser(false);
                setIsLoadedUserMe(true);
            } else {
                if (userProfileUuid) {
                    const response = await apiWorker.getUserProfiles(
                        { uuid: userProfileUuid },
                        token
                    );

                    if (
                        response.meta.items.total === 1 &&
                        response.data[0].uuid === userProfileUuid
                    ) {
                        setLoadedUser(response.data[0]);
                        setFollowers(response.data[0].follows.followers);
                        setFollowing(response.data[0].follows.following);
                        setLoadedUserImage(response.data[0].image.main);
                        setUserFollowing(
                            response.data[0].userFollowing ?? false
                        );
                        setLoadingUser(false);
                    } else {
                        setLoadingUser(false);
                        setNoResultsFound(true);
                    }
                } else {
                    if (username) {
                        const response = await apiWorker.getUserProfiles(
                            { username: username },
                            token
                        );

                        if (
                            response.meta.items.total === 1 &&
                            response.data[0].username === username
                        ) {
                            setLoadedUser(response.data[0]);
                            setLoadingUser(false);
                            setFollowers(response.data[0].follows.followers);
                            setFollowing(response.data[0].follows.following);
                            setLoadedUserImage(response.data[0].image.main);
                            setUserFollowing(
                                response.data[0].userFollowing ?? false
                            );
                        } else {
                            setLoadingUser(false);
                            setNoResultsFound(true);
                        }
                    } else {
                        setNoResultsFound(true);
                    }
                }
            }

            setPageLoaded(true);
        };

        getUserForPage()
            .then(() => {})
            .catch((error) => {
                console.log('Error fetching user:', error);
            });
    }, [username, userProfileUuid, token, fresh]);

    return pageLoaded ? (
        <>
            <Fade in={pageLoaded} timeout={fadeTimeout}>
                <div>
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                        {noResultsFound ? (
                            <NoResultsFound />
                        ) : (
                            !loadingUser &&
                            loadedUser && (
                                <>
                                    <PageTopSection
                                        isMobile={isMobile}
                                        padding={padding}
                                        hideSeperator={true}
                                        gapSize={'small'}
                                    />
                                    <Box
                                        sx={{
                                            paddingLeft: getOverallPadding(
                                                isMobile,
                                                padding
                                            ),
                                            paddingRight: getOverallPadding(
                                                isMobile,
                                                padding
                                            ),
                                        }}
                                    >
                                        <Grid
                                            container
                                            spacing={isMobile ? 1 : 3}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'stretch',
                                            }}
                                        >
                                            {/* Left Side: Responsive Image */}
                                            <Grid
                                                item
                                                xs={
                                                    width && width >= 700
                                                        ? 'auto'
                                                        : 12
                                                }
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <UserProfileImage
                                                    loadedUser={loadedUser}
                                                    user={user}
                                                    loadedUserImage={
                                                        loadedUserImage
                                                    }
                                                    uploadImage={uploadImage}
                                                    setUploadImage={
                                                        setUploadImage
                                                    }
                                                />
                                            </Grid>

                                            {/* Right Side: Content */}
                                            <Grid
                                                item
                                                xs
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                {/* Name and Role Row */}
                                                <Grid container spacing={0}>
                                                    {/* Left Section: Username and Account Type */}
                                                    <Grid
                                                        item
                                                        xs={
                                                            width &&
                                                            width >= 700
                                                                ? 6
                                                                : 12
                                                        }
                                                        sx={{
                                                            display:
                                                                width &&
                                                                width >= 700
                                                                    ? 'flex'
                                                                    : undefined,
                                                            flexDirection:
                                                                'column',
                                                            alignItems:
                                                                'flex-start',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="h2"
                                                            color={
                                                                theme.palette
                                                                    .text
                                                                    .secondary
                                                            }
                                                            sx={{
                                                                cursor: 'default',
                                                            }}
                                                            gutterBottom
                                                        >
                                                            {
                                                                loadedUser.username
                                                            }
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            color={
                                                                theme.palette
                                                                    .text
                                                                    .disabled
                                                            }
                                                            sx={{
                                                                cursor: 'default',
                                                            }}
                                                            gutterBottom
                                                        >
                                                            {upperCaseFirst(
                                                                loadedUser.accountType
                                                            )}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Right Section */}
                                                    <Grid
                                                        item
                                                        xs={
                                                            width &&
                                                            width >= 700
                                                                ? 6
                                                                : 12
                                                        }
                                                        sx={{
                                                            display:
                                                                width &&
                                                                width >= 700
                                                                    ? 'flex'
                                                                    : undefined,
                                                            flexDirection:
                                                                'column',
                                                            alignItems:
                                                                'flex-end',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            color={
                                                                theme.palette
                                                                    .text
                                                                    .disabled
                                                            }
                                                            gutterBottom
                                                            sx={{
                                                                cursor: 'default',
                                                            }}
                                                        >
                                                            {/* Followers */}
                                                            <Box
                                                                component="span"
                                                                onClick={
                                                                    user &&
                                                                    user.uuid ===
                                                                        loadedUser.uuid
                                                                        ? () =>
                                                                              showFollowersUsersModal()
                                                                        : undefined
                                                                }
                                                                sx={{
                                                                    display:
                                                                        'inline',
                                                                    cursor:
                                                                        user &&
                                                                        user.uuid ===
                                                                            loadedUser.uuid
                                                                            ? 'pointer'
                                                                            : 'default',
                                                                    '&:hover .hover-target':
                                                                        {
                                                                            textDecoration:
                                                                                user &&
                                                                                user.uuid ===
                                                                                    loadedUser.uuid
                                                                                    ? 'underline'
                                                                                    : 'none',
                                                                        },
                                                                }}
                                                            >
                                                                <Box
                                                                    component="span"
                                                                    className="hover-target"
                                                                    sx={{
                                                                        display:
                                                                            'inline',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                        }}
                                                                    >
                                                                        {
                                                                            followers
                                                                        }
                                                                    </span>
                                                                    &nbsp;
                                                                    <span>
                                                                        followers
                                                                    </span>
                                                                </Box>
                                                            </Box>

                                                            {/* Dot separator */}
                                                            <span
                                                                style={{
                                                                    marginLeft: 13,
                                                                    marginRight: 13,
                                                                }}
                                                            >
                                                                ·
                                                            </span>

                                                            {/* Following */}
                                                            <Box
                                                                component="span"
                                                                onClick={
                                                                    user &&
                                                                    user.uuid ===
                                                                        loadedUser.uuid
                                                                        ? () =>
                                                                              showFollowingUsersModal()
                                                                        : undefined
                                                                }
                                                                sx={{
                                                                    display:
                                                                        'inline',
                                                                    cursor:
                                                                        user &&
                                                                        user.uuid ===
                                                                            loadedUser.uuid
                                                                            ? 'pointer'
                                                                            : 'default',
                                                                    '&:hover .hover-target':
                                                                        {
                                                                            textDecoration:
                                                                                user &&
                                                                                user.uuid ===
                                                                                    loadedUser.uuid
                                                                                    ? 'underline'
                                                                                    : 'none',
                                                                        },
                                                                }}
                                                            >
                                                                <Box
                                                                    component="span"
                                                                    className="hover-target"
                                                                    sx={{
                                                                        display:
                                                                            'inline',
                                                                    }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontWeight:
                                                                                'bold',
                                                                            color: theme
                                                                                .palette
                                                                                .text
                                                                                .secondary,
                                                                        }}
                                                                    >
                                                                        {
                                                                            following
                                                                        }
                                                                    </span>
                                                                    &nbsp;
                                                                    <span>
                                                                        following
                                                                    </span>
                                                                </Box>
                                                            </Box>
                                                        </Typography>

                                                        {user &&
                                                            (user.uuid !==
                                                            loadedUser.uuid ? (
                                                                <Button
                                                                    variant="contained"
                                                                    type="submit"
                                                                    onClick={async () => {
                                                                        if (
                                                                            token &&
                                                                            user &&
                                                                            loadedUser
                                                                        ) {
                                                                            try {
                                                                                await apiWorker.postUserProfileFollow(
                                                                                    loadedUser.uuid,
                                                                                    token
                                                                                );

                                                                                if (
                                                                                    userFollowing
                                                                                ) {
                                                                                    setFollowers(
                                                                                        followers -
                                                                                            1
                                                                                    );
                                                                                    setUserFollowing(
                                                                                        false
                                                                                    );
                                                                                } else {
                                                                                    setFollowers(
                                                                                        followers +
                                                                                            1
                                                                                    );
                                                                                    setUserFollowing(
                                                                                        true
                                                                                    );
                                                                                }
                                                                            } catch (error) {
                                                                                console.warn(
                                                                                    FOLLOW_USER_FAILED
                                                                                );
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {userFollowing
                                                                        ? 'UNFOLLOW'
                                                                        : 'FOLLOW'}
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="contained"
                                                                    type="submit"
                                                                    onClick={
                                                                        showUpdateUserModal
                                                                    }
                                                                >
                                                                    Edit Profile
                                                                </Button>
                                                            ))}
                                                    </Grid>
                                                </Grid>

                                                {/* Description */}
                                                <Card
                                                    sx={{
                                                        flexGrow: 1,
                                                        paddingLeft: 2,
                                                        paddingRight: 2,
                                                        paddingTop: 1,
                                                        paddingBottom: 1,
                                                        marginTop: 2,
                                                        textAlign: 'left',
                                                        whiteSpace: 'pre-line',
                                                        wordBreak: 'break-word',
                                                        cursor: 'default',
                                                    }}
                                                >
                                                    {loadedUser.description
                                                        ? loadedUser.description
                                                        : UserProfileDefaultDescription}
                                                </Card>
                                            </Grid>
                                        </Grid>
                                        <Grid sx={{ marginTop: 2 }}>
                                            <Socials
                                                twitter={loadedUser.twitter}
                                                patreon={loadedUser.patreon}
                                                facebook={loadedUser.facebook}
                                                paypal={loadedUser.paypal}
                                                youtube={loadedUser.youtube}
                                                buttonStyle={'chip'}
                                            />
                                        </Grid>
                                        <PageTopSection
                                            isMobile={isMobile}
                                            padding={padding}
                                            gapSize={'small'}
                                        />
                                        <Grid item xs={12}>
                                            <SearchResults
                                                loadingResults={loadingResults}
                                                isMobile={isMobile}
                                                uploads={uploads}
                                                previewCardSizes={
                                                    PreviewCardSizeOthers
                                                }
                                                lastPage={lastPage}
                                                currentPage={currentPage}
                                                handlePageChange={
                                                    handlePageChange
                                                }
                                                user={user}
                                                count={uploadParams.count}
                                            />
                                        </Grid>
                                    </Box>
                                    {user && user.uuid === loadedUser.uuid && (
                                        <>
                                            <UpdateUserModal
                                                isModalOpen={
                                                    isUpdateUserModalOpen
                                                }
                                                isMobile={isMobile}
                                                loadedUser={loadedUser}
                                                setError={setError}
                                                error={error}
                                                setSuccess={setSuccess}
                                                success={success}
                                                setLoadedUser={setLoadedUser}
                                                closeModal={
                                                    closeUpdateUserModal
                                                }
                                            />

                                            <FollowingUsersModal
                                                isMobile={isMobile}
                                                isModalOpen={
                                                    isFollowingUsersModalOpen
                                                }
                                                closeModal={
                                                    closeFollowingUsersModal
                                                }
                                                setError={setError}
                                                error={error}
                                                setFollowers={setFollowers}
                                                setFollowing={setFollowing}
                                                followers={followers}
                                                following={following}
                                            />

                                            <FollowersUsersModal
                                                isMobile={isMobile}
                                                isModalOpen={
                                                    isFollowersUsersModalOpen
                                                }
                                                closeModal={
                                                    closeFollowersUsersModal
                                                }
                                                setError={setError}
                                                error={error}
                                                setFollowers={setFollowers}
                                                setFollowing={setFollowing}
                                                followers={followers}
                                                following={following}
                                            />
                                        </>
                                    )}
                                </>
                            )
                        )}
                    </Box>
                </div>
            </Fade>
        </>
    ) : null;
};

export default ViewUserPage;
